/* Turns one of the Apex 25 legal markdown files (./docs/*.md) into blocks the page renders. The
   files use a small subset of markdown: one "# title", an "Effective date:" line, "## N. Heading"
   sections, single-line paragraphs, "* " or "- " bullets, pipe tables, and **bold** / *italic*
   inline. Keep in step with the same file in theresume's first-round/legal. */

export type LegalBlock =
  | { type: "heading"; id: string; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | { type: "table"; header: string[]; rows: string[][] };

export type LegalDoc = {
  title: string;
  /** The text after "Effective date:", e.g. "1 October 2026". */
  effective?: string;
  blocks: LegalBlock[];
};

export type LegalHeading = Extract<LegalBlock, { type: "heading" }>;
export type LegalTable = Extract<LegalBlock, { type: "table" }>;

const EFFECTIVE = /^Effective date:\s*(.+)$/;
const BULLET = /^[*-] /;

const slug = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/** "## 4. How AI is used" gets the stable id "section-4", so links survive a heading rewording. */
const headingId = (text: string) => {
  const number = /^(\d+)\.\s/.exec(text)?.[1];
  return number ? `section-${number}` : slug(text);
};

const cells = (row: string) =>
  row
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((cell) => cell.trim());

export const parseLegalDoc = (markdown: string): LegalDoc => {
  const doc: LegalDoc = { title: "", blocks: [] };
  let list: string[] | null = null;
  let table: string[] | null = null;

  const closeList = () => {
    if (list) doc.blocks.push({ type: "list", items: list });
    list = null;
  };
  const closeTable = () => {
    if (table) {
      const [header, , ...rows] = table.map(cells);
      doc.blocks.push({ type: "table", header: header ?? [], rows });
    }
    table = null;
  };

  for (const raw of markdown.split("\n")) {
    const line = raw.trimEnd();

    if (line.startsWith("|")) {
      closeList();
      (table ??= []).push(line);
      continue;
    }
    closeTable();

    // Bullets in the source can be separated by blank lines, so a list stays open across blanks.
    if (BULLET.test(line)) {
      (list ??= []).push(line.slice(2).trim());
      continue;
    }
    if (line === "") continue;
    closeList();

    if (line.startsWith("# ")) {
      doc.title = line.slice(2).trim();
    } else if (line.startsWith("## ")) {
      const text = line.slice(3).trim();
      doc.blocks.push({ type: "heading", id: headingId(text), text });
    } else if (EFFECTIVE.test(line)) {
      doc.effective = EFFECTIVE.exec(line)?.[1];
    } else {
      doc.blocks.push({ type: "paragraph", text: line });
    }
  }
  closeList();
  closeTable();
  return doc;
};

export const headingsOf = (doc: LegalDoc) =>
  doc.blocks.filter((block): block is LegalHeading => block.type === "heading");
export const tablesOf = (doc: LegalDoc) =>
  doc.blocks.filter((block): block is LegalTable => block.type === "table");

export type InlineToken =
  | { type: "text"; text: string }
  | { type: "bold" | "italic"; text: string }
  | { type: "link"; text: string; href: string };

// **bold**, *italic*, an email address, or a www. address.
const INLINE =
  /(\*\*[^*]+\*\*|\*[^*]+\*|[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+|www\.[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+)/g;

export const tokenizeInline = (text: string): InlineToken[] =>
  text
    .split(INLINE)
    .filter((part) => part !== "")
    .map((part): InlineToken => {
      if (part.startsWith("**") && part.endsWith("**") && part.length > 4)
        return { type: "bold", text: part.slice(2, -2) };
      if (part.startsWith("*") && part.endsWith("*") && part.length > 2)
        return { type: "italic", text: part.slice(1, -1) };
      if (part.includes("@")) return { type: "link", text: part, href: `mailto:${part}` };
      if (part.startsWith("www.")) return { type: "link", text: part, href: `https://${part}` };
      return { type: "text", text: part };
    });
